import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, ArrowLeft, Eye, EyeOff, KeyRound } from 'lucide-react';
import { merchantAuthAPI } from '../lib/api';
import { useMerchantAuth } from '../contexts/MerchantAuthContext';
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '../lib/analytics';
import { cabinetPath } from '../lib/domains';

interface MerchantAuthPageProps {
    mode: 'login' | 'register';
}

export const MerchantAuthPage: React.FC<MerchantAuthPageProps> = ({ mode }) => {
    const navigate = useNavigate();
    const { refreshUser } = useMerchantAuth();
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        company_name: ''
    });
    const [loading, setLoading] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resetMessage, setResetMessage] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [twoFactorChallenge, setTwoFactorChallenge] = useState<string | null>(null);
    const [twoFactorCode, setTwoFactorCode] = useState('');

    const isLogin = mode === 'login';

    const finishLogin = async (accessToken: string, authMode: 'login' | 'register') => {
        localStorage.setItem('merchant_access_token', accessToken);
        await refreshUser();
        trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_SUCCEEDED, {
            user_type: 'merchant',
            auth_mode: authMode,
        });
        navigate(cabinetPath('merchant', '/dashboard'));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            if (isLogin && twoFactorChallenge) {
                const response = await merchantAuthAPI.completeTwoFactorLogin(
                    twoFactorChallenge,
                    twoFactorCode
                );
                await finishLogin(response.access_token, 'login');
                return;
            }

            if (isLogin) {
                console.log('Попытка логина мерчанта:', formData.username);
                const response = await merchantAuthAPI.login({
                    username: formData.username,
                    password: formData.password
                });
                if ('two_factor_required' in response) {
                    setTwoFactorChallenge(response.challenge_token);
                    setFormData(prev => ({ ...prev, password: '' }));
                    return;
                }
                await finishLogin(response.access_token, 'login');
            } else {
                // Регистрация
                if (formData.password.length < 10) {
                    setError('Пароль мерчанта должен содержать минимум 10 символов');
                    return;
                }
                if (formData.password !== formData.confirmPassword) {
                    setError('Пароли не совпадают');
                    return;
                }

                await merchantAuthAPI.register({
                    username: formData.username,
                    email: formData.email,
                    password: formData.password,
                    company_name: formData.company_name || undefined
                });

                // После успешной регистрации сразу логинимся
                const response = await merchantAuthAPI.login({
                    username: formData.username,
                    password: formData.password
                });
                if ('two_factor_required' in response) {
                    setTwoFactorChallenge(response.challenge_token);
                    return;
                }
                await finishLogin(response.access_token, 'register');
            }
        } catch (err: any) {
            const message = err.response?.data?.detail || 'Произошла ошибка';
            trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_FAILED, {
                user_type: 'merchant',
                auth_mode: isLogin ? 'login' : 'register',
                error_message: message,
            });
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        if (error) setError(null);
        if (resetMessage) setResetMessage(null);
    };

    const handlePasswordResetRequest = async () => {
        const identifier = formData.username.trim();
        setError(null);
        setResetMessage(null);

        if (!identifier) {
            setError('Введите имя пользователя или email');
            return;
        }

        setResetLoading(true);
        try {
            const response = await merchantAuthAPI.requestPasswordReset(identifier);
            setResetMessage(response.message);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Не удалось отправить запрос');
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <Link to="/" className="inline-flex items-center text-green-600 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200 mb-4">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Назад к выбору
                    </Link>
                    <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-950 rounded-full flex items-center justify-center mb-4">
                        <Building2 className="h-8 w-8 text-green-600 dark:text-green-300" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-slate-100">
                        {twoFactorChallenge
                            ? 'Подтверждение входа'
                            : (isLogin ? 'Вход для мерчантов' : 'Регистрация мерчанта')}
                    </h2>
                    <p className="mt-2 text-sm text-gray-600 dark:text-slate-300">
                        {isLogin
                            ? (twoFactorChallenge
                                ? 'Откройте Google Authenticator'
                                : 'Войдите в свой аккаунт мерчанта')
                            : 'Создайте аккаунт мерчанта для интеграции платежей'
                        }
                    </p>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>
                            {twoFactorChallenge
                                ? 'Код Google Authenticator'
                                : (isLogin ? 'Авторизация' : 'Создание аккаунта')}
                        </CardTitle>
                        <CardDescription>
                            {isLogin
                                ? (twoFactorChallenge
                                    ? 'Введите шестизначный код из приложения'
                                    : 'Введите свои учетные данные')
                                : 'Заполните информацию для регистрации'
                            }
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div className="p-3 text-sm text-red-500 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
                                    {error}
                                </div>
                            )}
                            {resetMessage && (
                                <div className="p-3 text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md">
                                    {resetMessage}
                                </div>
                            )}

                            {twoFactorChallenge ? (
                                <div className="space-y-2">
                                    <Label htmlFor="twoFactorCode">Код подтверждения</Label>
                                    <Input
                                        id="twoFactorCode"
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        value={twoFactorCode}
                                        onChange={(e) => {
                                            setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                                            if (error) setError(null);
                                        }}
                                        placeholder="000000"
                                        className="text-center font-mono text-2xl tracking-[0.35em]"
                                        pattern="\d{6}"
                                        maxLength={6}
                                        autoFocus
                                        required
                                        disabled={loading}
                                    />
                                </div>
                            ) : (
                            <>
                            <div className="space-y-2">
                                <Label htmlFor="username">Имя пользователя</Label>
                                <Input
                                    id="username"
                                    type="text"
                                    value={formData.username}
                                    onChange={(e) => handleInputChange('username', e.target.value)}
                                    placeholder="Введите имя пользователя"
                                    required
                                    disabled={loading}
                                />
                            </div>

                            {!isLogin && (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                            placeholder="Введите email"
                                            required
                                            disabled={loading}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="company_name">Название компании (опционально)</Label>
                                        <Input
                                            id="company_name"
                                            type="text"
                                            value={formData.company_name}
                                            onChange={(e) => handleInputChange('company_name', e.target.value)}
                                            placeholder="Введите название компании"
                                            disabled={loading}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="password">Пароль</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.password}
                                        onChange={(e) => handleInputChange('password', e.target.value)}
                                        placeholder="Введите пароль"
                                        required
                                        disabled={loading}
                                        minLength={isLogin ? 6 : 10}
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-2 top-1/2 -translate-y-1/2 h-auto p-1"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                            {!isLogin && (
                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                    Не менее 10 символов
                                </p>
                            )}

                            {!isLogin && (
                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Подтвердите пароль</Label>
                                    <div className="relative">
                                        <Input
                                            id="confirmPassword"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={formData.confirmPassword}
                                            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                            placeholder="Повторите пароль"
                                            required
                                            disabled={loading}
                                            minLength={10}
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-2 top-1/2 -translate-y-1/2 h-auto p-1"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        >
                                            {showConfirmPassword ? (
                                                <EyeOff className="h-4 w-4" />
                                            ) : (
                                                <Eye className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            )}
                            </>
                            )}

                            <Button
                                type="submit"
                                className="w-full bg-green-600 hover:bg-green-700"
                                disabled={loading || resetLoading}
                            >
                                {loading
                                    ? 'Обработка...'
                                    : (twoFactorChallenge
                                        ? 'Подтвердить вход'
                                        : (isLogin ? 'Войти' : 'Зарегистрироваться'))}
                            </Button>
                            {isLogin && !twoFactorChallenge && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className="w-full text-green-700 dark:text-green-300"
                                    onClick={handlePasswordResetRequest}
                                    disabled={loading || resetLoading}
                                >
                                    <KeyRound className="h-4 w-4 mr-2" />
                                    {resetLoading ? 'Отправка...' : 'Забыли пароль?'}
                                </Button>
                            )}
                            {twoFactorChallenge && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className="w-full"
                                    disabled={loading}
                                    onClick={() => {
                                        setTwoFactorChallenge(null);
                                        setTwoFactorCode('');
                                        setError(null);
                                    }}
                                >
                                    Вернуться к вводу пароля
                                </Button>
                            )}
                        </form>

                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
